import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileDropdowns, setMobileDropdowns] = useState({
    sports: false,
    construction: false
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const sportsOptions = [
    { name: 'Pickleball Courts', slug: 'pickleball-courts' },
    { name: 'Football Turf', slug: 'football-turf' },
    { name: 'Cricket Turf', slug: 'cricket-turf' },
    { name: 'Padel Courts', slug: 'padel-courts' },
    { name: 'Badminton Courts', slug: 'badminton-courts' },
    { name: 'Athlete Track', slug: 'athlete-track' },
    { name: 'Basketball Courts', slug: 'basketball-courts' },
    { name: 'Volleyball Courts', slug: 'volleyball-courts' },
    { name: 'Squash Courts', slug: 'squash-courts' },
    { name: 'Tennis Courts', slug: 'tennis-courts' }
  ];

  const constructionOptions = [
    { name: 'Warehouse', slug: 'warehouse' },
    { name: 'Court Structures', slug: 'court-structures' }
  ];

  const handleDropdownToggle = (dropdown) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  const closeDropdown = () => {
    setActiveDropdown(null);
  };

  const toggleMobileDropdown = (dropdown) => {
    setMobileDropdowns(prev => ({
      ...prev,
      [dropdown]: !prev[dropdown]
    }));
  };

  const closeMobileMenu = () => {
    setIsMenuOpen(false);
    setMobileDropdowns({ sports: false, construction: false });
  };

  return (
    <header className={`modern-header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="header-content">
        <Link to="/" className="logo-container" onClick={closeDropdown}>
          <img 
            src="https://customer-assets.emergentagent.com/job_8df07f88-bc0e-485a-bdfa-23d100a34040/artifacts/yx81ddm2_rjlogo%20%283%29.png" 
            alt="Right Shade Logo" 
            className="logo-img"
          />
        </Link>
        
        <nav className="nav-menu">
          <ul style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-lg)', listStyle: 'none', margin: 0, padding: 0 }}>
            <li className="nav-item">
              <Link to="/" className="nav-link" onClick={closeDropdown}>
                Home
              </Link>
            </li>
            
            <li className="nav-item">
              <Link to="/projects" className="nav-link" onClick={closeDropdown}>
                Projects
              </Link>
            </li>
            
            <li className="nav-item">
              <div style={{ position: 'relative' }}>
                <Link 
                  to="/sports"
                  className="nav-link"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                  onMouseEnter={() => handleDropdownToggle('sports')}
                  onClick={closeDropdown}
                >
                  Sports <ChevronDown size={16} />
                </Link>
                {activeDropdown === 'sports' && (
                  <div className="dropdown-menu" onMouseLeave={closeDropdown}>
                    {sportsOptions.map((option, index) => (
                      <Link 
                        key={index} 
                        to={`/sports/${option.slug}`}
                        className="dropdown-item"
                        onClick={closeDropdown}
                      >
                        {option.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </li>

            <li className="nav-item">
              <div style={{ position: 'relative' }}>
                <Link 
                  to="/construction"
                  className="nav-link"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                  onMouseEnter={() => handleDropdownToggle('construction')}
                  onClick={closeDropdown}
                >
                  Construction <ChevronDown size={16} />
                </Link>
                {activeDropdown === 'construction' && (
                  <div className="dropdown-menu" onMouseLeave={closeDropdown}>
                    {constructionOptions.map((option, index) => (
                      <Link 
                        key={index} 
                        to={`/construction/${option.slug}`}
                        className="dropdown-item"
                        onClick={closeDropdown}
                      >
                        {option.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </li>

            <li className="nav-item">
              <Link to="/about" className="nav-link" onClick={closeDropdown}>
                About Us
              </Link>
            </li>
            
            <li className="nav-item">
              <Link to="/contact" className="nav-link" onClick={closeDropdown}>
                Contact
              </Link>
            </li>
            
            <li className="nav-item">
              <Link to="/blogs" className="nav-link" onClick={closeDropdown}>
                Blogs
              </Link>
            </li>
          </ul>
        </nav>

        <button 
          className="mobile-menu-toggle"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          style={{
            display: 'none',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'white',
            padding: '8px',
            transition: 'all 0.3s ease'
          }}
        >
          {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="mobile-menu" style={{
          position: 'fixed',
          top: '80px',
          left: 0,
          right: 0,
          background: '#1A1B1E',
          boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
          padding: '20px',
          borderTop: '1px solid rgba(255, 215, 0, 0.2)',
          maxHeight: 'calc(100vh - 80px)',
          overflowY: 'auto',
          zIndex: 9999
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Link 
              to="/" 
              style={{
                color: 'white',
                textDecoration: 'none',
                padding: '12px 16px',
                borderRadius: '8px',
                transition: 'background 0.2s',
                fontSize: '16px'
              }}
              onClick={closeMobileMenu}
              onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 215, 0, 0.1)'}
              onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
            >
              Home
            </Link>
            
            <Link 
              to="/projects" 
              style={{
                color: 'white',
                textDecoration: 'none',
                padding: '12px 16px',
                borderRadius: '8px',
                transition: 'background 0.2s',
                fontSize: '16px'
              }}
              onClick={closeMobileMenu}
              onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 215, 0, 0.1)'}
              onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
            >
              Projects
            </Link>
            
            {/* Sports Dropdown */}
            <div>
              <div
                onClick={() => toggleMobileDropdown('sports')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  color: 'white',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  background: mobileDropdowns.sports ? 'rgba(255, 215, 0, 0.1)' : 'transparent',
                  transition: 'background 0.2s',
                  fontSize: '16px'
                }}
              >
                <span>Sports</span>
                <ChevronDown 
                  size={20} 
                  style={{
                    transform: mobileDropdowns.sports ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s'
                  }}
                />
              </div>
              
              {mobileDropdowns.sports && (
                <div style={{
                  paddingLeft: '16px',
                  marginTop: '4px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px'
                }}>
                  <Link 
                    to="/sports"
                    style={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      textDecoration: 'none',
                      padding: '10px 16px',
                      borderRadius: '6px',
                      fontSize: '15px',
                      transition: 'background 0.2s'
                    }}
                    onClick={closeMobileMenu}
                    onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 215, 0, 0.08)'}
                    onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    View All Sports
                  </Link>
                  {sportsOptions.map((option, index) => (
                    <Link 
                      key={index} 
                      to={`/sports/${option.slug}`}
                      style={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        textDecoration: 'none',
                        padding: '10px 16px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        transition: 'background 0.2s'
                      }}
                      onClick={closeMobileMenu}
                      onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 215, 0, 0.08)'}
                      onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      {option.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            
            {/* Construction Dropdown */}
            <div>
              <div
                onClick={() => toggleMobileDropdown('construction')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  color: 'white',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  background: mobileDropdowns.construction ? 'rgba(255, 215, 0, 0.1)' : 'transparent',
                  transition: 'background 0.2s',
                  fontSize: '16px'
                }}
              >
                <span>Construction</span>
                <ChevronDown 
                  size={20} 
                  style={{
                    transform: mobileDropdowns.construction ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s'
                  }}
                />
              </div>
              
              {mobileDropdowns.construction && (
                <div style={{
                  paddingLeft: '16px',
                  marginTop: '4px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px'
                }}>
                  <Link 
                    to="/construction"
                    style={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      textDecoration: 'none',
                      padding: '10px 16px',
                      borderRadius: '6px',
                      fontSize: '15px',
                      transition: 'background 0.2s'
                    }}
                    onClick={closeMobileMenu}
                    onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 215, 0, 0.08)'}
                    onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    View All Construction
                  </Link>
                  {constructionOptions.map((option, index) => (
                    <Link 
                      key={index} 
                      to={`/construction/${option.slug}`}
                      style={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        textDecoration: 'none',
                        padding: '10px 16px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        transition: 'background 0.2s'
                      }}
                      onClick={closeMobileMenu}
                      onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 215, 0, 0.08)'}
                      onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      {option.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            
            <Link 
              to="/about" 
              style={{
                color: 'white',
                textDecoration: 'none',
                padding: '12px 16px',
                borderRadius: '8px',
                transition: 'background 0.2s',
                fontSize: '16px'
              }}
              onClick={closeMobileMenu}
              onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 215, 0, 0.1)'}
              onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
            >
              About Us
            </Link>
            
            <Link 
              to="/contact" 
              style={{
                color: 'white',
                textDecoration: 'none',
                padding: '12px 16px',
                borderRadius: '8px',
                transition: 'background 0.2s',
                fontSize: '16px'
              }}
              onClick={closeMobileMenu}
              onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 215, 0, 0.1)'}
              onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
            >
              Contact
            </Link>
            
            <Link 
              to="/blogs" 
              style={{
                color: 'white',
                textDecoration: 'none',
                padding: '12px 16px',
                borderRadius: '8px',
                transition: 'background 0.2s',
                fontSize: '16px'
              }}
              onClick={closeMobileMenu}
              onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 215, 0, 0.1)'}
              onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
            >
              Blogs
            </Link>
          </div>
        </div>
      )}

      <style jsx>{`
        .modern-header.scrolled {
          background: rgba(26, 27, 30, 0.98);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }
        
        .mobile-menu {
          position: fixed !important;
          z-index: 9999 !important;
        }

        @media (max-width: 1024px) {
          .nav-menu {
            display: none;
          }
          
          .mobile-menu-toggle {
            display: block !important;
          }
          
          .mobile-menu-toggle:hover {
            background: rgba(255, 215, 0, 0.2);
            border-radius: 8px;
          }
          
          .mobile-menu-toggle:active {
            transform: scale(0.95);
          }
        }
        
        @media (max-width: 768px) {
          .header-content {
            height: 60px !important;
          }
          
          .logo-img {
            height: 40px !important;
          }
          
          .mobile-menu {
            top: 60px !important;
            max-height: calc(100vh - 60px) !important;
          }
        }
      `}</style>
    </header>
  );
};

export default Header;